import { EventEmitter, Injectable, Injector } from '@angular/core';
import { NavigationBar, FEDERATED_SERVICE_ID_TYPE, IAuth0Config, IContextSwitcherOptions, INavBarOptions } from "@opengov/components-nav-bar-bundled";
import { RouteService } from './route.service';
import { environment } from 'src/environments/environment';
import { Store } from '@ngrx/store';
import { getUserProfile } from '../state/_selectors/user.selector';
import { filter, take, distinctUntilChanged } from 'rxjs/operators';
import { StorageService } from './storage.service';
import { ApplicationService } from '../state/_services/application.service';
import { LaunchDarklyService } from './launchdarkly.service';
import { FEATURE_FLAGS } from 'src/app/shared/launchdarkly.constants';

@Injectable({
  providedIn: 'root'
})
export class NavigationService {

  showGlobalNav = new EventEmitter();
  currentAppName: string = 'App Builder';
  private lastAppName: string = 'App Builder';
  private lastEntityFriendlyId: string | null = null;

  private isInIframe(): boolean {
    try {
      return window.self !== window.top;
    } catch {
      // SecurityError: cross-origin frame
      return true;
    }
  }

  constructor(
    private readonly routeService: RouteService,
    private readonly injector: Injector,
    private readonly store: Store,
    private readonly storage: StorageService,
    private readonly applicationService: ApplicationService,
    private readonly ldService: LaunchDarklyService
  ) {
    this.listenToGlobalNavChanges();
    this.listenToEntityFriendlyIdChanges();
    this.ldService
      .getFlagValue(FEATURE_FLAGS.ENABLE_SINGLE_APP_DIRECT_LOGIN, false)
      .pipe(distinctUntilChanged())
      .subscribe(() => this.refreshGlobalNavIfVisible());
  }

  /** Re-run global nav bar when isSingleAppUser$ changes (e.g. after /apps probe in HomeComponent). */
  refreshGlobalNavIfVisible(): void {
    const container = document.getElementById('GlobalNavContainer');
    if (container && container.style.display !== 'none') {
      this.setGlobalNav();
    }
  }

  private listenToGlobalNavChanges() {
    this.showGlobalNav.subscribe((action) => {
      const container = document.getElementById("GlobalNavContainer");
      const sidebar = document.getElementById("side-navigation");
      const mainContent = document.getElementById("wrapper");
      if (container) {
        if (this.isInIframe()) {
          container.style.display = 'none';
          return;
        }
        if (action === 'show') {
          this.setGlobalNav();
          container.style.display = 'block';
          sidebar?.style.setProperty('top', '50px', 'important');
          mainContent?.style.setProperty('top', '50px', 'important');
          mainContent?.style.setProperty('position', 'relative', 'important');
          document.body.classList.add('global-nav-visible');
          this.addDynamicStyles();
        } else if (action === 'hide') {
          container.style.display = 'none';
          sidebar?.style.setProperty('top', '0px', 'important');
          mainContent?.style.setProperty('top', '0px', 'important');
          mainContent?.style.removeProperty('position');
          document.body.classList.remove('global-nav-visible');
          this.removeDynamicStyles();
        }
      }
    });
  }

  /**
   * App Library entry for global nav, or empty when single-app direct login hides it.
   * Kept as a single place so unit tests can assert menu shape without spying on NavigationBar.
   */
  private getGlobalNavMenuOptions(): NonNullable<INavBarOptions['menuOptions']> {
    const singleAppDirectLoginEnabled = !!this.ldService.getFlagValueSync(
      FEATURE_FLAGS.ENABLE_SINGLE_APP_DIRECT_LOGIN,
      false
    );
    const hideAppLibrary =
      singleAppDirectLoginEnabled &&
      this.applicationService.isSingleAppUser$.getValue() === true;
    return !hideAppLibrary
      ? [
          {
            label: 'App Library',
            tooltipText: 'Show all applications',
            id: 'uuid_for_menu_item',
            shouldFetchNestedMenuItems: true,
            openInNewTab: false,
            isActive:
              window.location.pathname === '/apps' ||
              window.location.pathname === '/apps/',
            onClick: () => {
              this.routeService.navigate(['/apps']);
            },
          },
        ]
      : [];
  }

  private setGlobalNav() {
    const menuOptions = this.getGlobalNavMenuOptions();

    // Get user profile from store (async) and initialize NavigationBar
    // Filter out null values to wait for profile to be loaded
    this.store.select(getUserProfile).pipe(
      filter(profile => profile?.Id != null),
      take(1)
    ).subscribe(userProfile => {
      // Get userUUID from userProfile
      let userUUID: string | null = userProfile?.Id || null;

      if (!userUUID) {
        return;
      }

      // Get entityFriendlyId from localStorage
      const entityFriendlyId = this.getEntityFriendlyIdFromStorage();

      const auth0Config: IAuth0Config = {
        auth0clientID: environment.OGAuth0ClientId,
        auth0domain: environment.OGAuth0Domain,
        auth0audience: environment.OGAuth0Audience,
        auth0callbackUrl: window.location.origin + '/callback-handler'
      };

      // Only create contextSwitcherOptions if entityFriendlyId has a value
      // GraphQL doesn't allow empty string for activeEntityId
      let contextSwitcherOptions: IContextSwitcherOptions | undefined = undefined;
      if (entityFriendlyId) {
        contextSwitcherOptions = {
          userUUID: userUUID,
          baseUrl: environment.controlPanelURL,
          activeEntityId: entityFriendlyId,
          activeEntityIdType: 'BY_FRIENDLY_ID' as FEDERATED_SERVICE_ID_TYPE,
          auth0Config : auth0Config
        };
      }

      const data: INavBarOptions = {
        appName: this.currentAppName,
        menuOptions: menuOptions
      };

      // Only include contextSwitcherOptions if it has a value
      if (contextSwitcherOptions) {
        data.contextSwitcherOptions = contextSwitcherOptions;
      }

      const container = document.getElementById("GlobalNavContainer");

      if (container) {
        // Check if we need to re-initialize (value changed) or if it's initial setup
        // Reinitialize if:
        // 1. Container is empty (first time initialization)
        // 2. entityFriendlyId changed (including transitioning from null/empty to having a value or vice versa)
        // 3. Only appName changed (lightweight update - reuses existing React root)
        const isEmpty = container.innerHTML.trim() === '' || container.children.length === 0;
        const entityChanged = this.lastEntityFriendlyId !== entityFriendlyId;
        const appNameChanged = this.lastAppName !== this.currentAppName;
        const needsReinit = !isEmpty && entityChanged;

        if (isEmpty || needsReinit) {
          if (needsReinit) {
            // For re-initialization, use double requestAnimationFrame to wait for React's render cycle
            // Replace the container element to avoid React errors from clearing innerHTML
            // This is necessary because NavigationBar doesn't handle re-initialization gracefully
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                // Double RAF ensures we're after React's render cycle
                const currentContainer = document.getElementById("GlobalNavContainer");
                if (currentContainer?.parentNode) {
                  const parent = currentContainer.parentNode;
                  // Store container properties
                  const containerId = currentContainer.id;
                  const containerClass = currentContainer.className || '';
                  const containerStyle = currentContainer.style.cssText;
                  const containerDisplay = currentContainer.style.display;

                  // Create new container (div wrapper for NavigationBar component)
                  const newContainer = document.createElement('div');
                  newContainer.id = containerId;
                  newContainer.className = containerClass;
                  newContainer.style.cssText = containerStyle;
                  newContainer.style.display = containerDisplay;

                  // Replace old container with new one
                  parent.replaceChild(newContainer, currentContainer);

                  // Track the values used for initialization
                  this.lastEntityFriendlyId = entityFriendlyId;
                  this.lastAppName = this.currentAppName;

                  // Initialize NavigationBar in the new container
                  NavigationBar(data, newContainer);
                  
                  // Add accessibility attributes to the nav element created by NavigationBar
                  this.enhanceNavigationAccessibility(newContainer);
                }
              });
            });
          } else {
            // Initial setup - just initialize
            // Track the values used for initialization
            this.lastEntityFriendlyId = entityFriendlyId;
            this.lastAppName = this.currentAppName;
            NavigationBar(data, container);
            
            // Add accessibility attributes to the nav element created by NavigationBar
            this.enhanceNavigationAccessibility(container);
          }
        } else if (appNameChanged) {
          // Lightweight update: only app name changed, entity is the same.
          // NavigationBar reuses the existing React root when called on the same container.
          this.lastAppName = this.currentAppName;
          NavigationBar(data, container);
        }
      }
    });
  }

  /**
   * Listen to localStorage changes for entityFriendlyId and re-initialize navbar
   */
  private listenToEntityFriendlyIdChanges() {
    // Listen to storage events (when localStorage is updated from other tabs/windows)
    window.addEventListener('storage', (e) => {
      if (e.key === 'activeEntityFriendlyId') {
        this.updateNavbarWithEntityFriendlyId();
      }
    });

    // Also listen to custom event for same-window updates
    window.addEventListener('entityFriendlyIdUpdated', () => {
      this.updateNavbarWithEntityFriendlyId();
    });
  }

  /**
   * Update navbar with entityFriendlyId from storage
   * Only updates if the value has actually changed
   */
  private updateNavbarWithEntityFriendlyId() {
    const container = document.getElementById("GlobalNavContainer");
    if (!container || container.style.display === 'none') {
      return;
    }

    const currentEntityFriendlyId = this.storage.getEntityFriendlyId();

    // Only update if the value has actually changed
    // Don't set lastEntityFriendlyId here - let setGlobalNav handle it after initialization
    if (currentEntityFriendlyId === this.lastEntityFriendlyId) {
      return;
    }

    // Simply re-initialize the navbar with the new entityFriendlyId
    // The setGlobalNav method will handle clearing and re-initializing
    this.setGlobalNav();
  }

  /**
   * Get entityFriendlyId from storage service
   */
  private getEntityFriendlyIdFromStorage(): string | null {
    return this.storage.getEntityFriendlyId();
  }

  /**
   * Set entityFriendlyId using storage service and update navbar
   */
  setEntityFriendlyId(entityFriendlyId: string | null): void {
    this.storage.setEntityFriendlyId(entityFriendlyId);
    // Dispatch custom event for same-window updates
    window.dispatchEvent(new Event('entityFriendlyIdUpdated'));
    // Update navbar if it's visible
    this.updateNavbarWithEntityFriendlyId();
  }


  addDynamicStyles() {
    const id = 'global-nav-styles';
    let style = document.getElementById(id) as HTMLStyleElement;

    if (!style) {
      style = document.createElement('style');
      style.id = id;
      document.head.appendChild(style);
    }

    const styles = `
    .modal-open .modal {
      top: 50px;
    }
    
    /* Focus-visible styling for Global Navigation */
    #GlobalNavContainer a:focus-visible,
    #GlobalNavContainer button:focus-visible,
    #GlobalNavContainer .MuiMenuItem-root:focus-visible,
    #GlobalNavContainer .MuiButtonBase-root:focus-visible {
      outline: 2px solid var(--interaction-focus, #4b3fff) !important;
      outline-offset: -2px;
      border-radius: 4px;
    }
    
    #GlobalNavContainer a:focus:not(:focus-visible),
    #GlobalNavContainer button:focus:not(:focus-visible),
    #GlobalNavContainer .MuiMenuItem-root:focus:not(:focus-visible),
    #GlobalNavContainer .MuiButtonBase-root:focus:not(:focus-visible) {
      outline: none;
    }
    `;

    style.innerHTML = styles;
  }

  removeDynamicStyles() {
    const id = 'global-nav-styles';
    const style = document.getElementById(id);
    if (style) {
      style.remove();
    }
  }

  /**
   * Enhance accessibility of the nav element created by NavigationBar component
   * Adds aria-label and tabindex to make it accessible for screen readers
   */
  private enhanceNavigationAccessibility(container: HTMLElement) {
    // Wait for NavigationBar to render its nav element
    setTimeout(() => {
      const navElement = container.querySelector('nav');
      if (navElement) {
        // Add accessibility attributes if not already present
        if (!navElement.hasAttribute('aria-label')) {
          navElement.setAttribute('aria-label', 'Global');
        }
        if (!navElement.hasAttribute('tabindex')) {
          navElement.setAttribute('tabindex', '-1');
        }
      }
    }, 100);
  }

}
